// Pure utilities for artist recent usage (no React/browser dependencies).

const MS_PER_DAY = 1000 * 60 * 60 * 24

export const DEFAULT_RECENT_USAGE_SCORING = Object.freeze({
    halfLifeDays: 14,
    recencyWeight: 0.7,
    frequencyWeight: 0.3,
    // Normalization anchor for frequency; log2(maxCount + 1) maps to 1.0
    maxCountForNormalization: 1000,
})

const isPlainObject = (value) =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const toNonNegativeNumberOrZero = (value) => {
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n) || n <= 0) {
        return 0
    }
    return n
}

const toPositiveIntOrNull = (value) => {
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n)) return null
    const int = Math.floor(n)
    return int > 0 ? int : null
}

export const calculateRecencyScore = ({ lastUsedAt, now, halfLifeDays }) => {
    const last = toNonNegativeNumberOrZero(lastUsedAt)
    if (!last) return 0

    const current = toNonNegativeNumberOrZero(now)
    if (!current) return 0

    const deltaDays = Math.max(0, (current - last) / MS_PER_DAY)
    const halfLife = toNonNegativeNumberOrZero(halfLifeDays)
    if (!halfLife) return 0

    return Math.exp(-deltaDays / halfLife)
}

export const calculateFrequencyScore = (count) => Math.log2(toNonNegativeNumberOrZero(count) + 1)

export const calculateTotalScore = (
    usage,
    {
        now,
        halfLifeDays = DEFAULT_RECENT_USAGE_SCORING.halfLifeDays,
        recencyWeight = DEFAULT_RECENT_USAGE_SCORING.recencyWeight,
        frequencyWeight = DEFAULT_RECENT_USAGE_SCORING.frequencyWeight,
        maxCountForNormalization = DEFAULT_RECENT_USAGE_SCORING.maxCountForNormalization,
    } = {}
) => {
    const recency = calculateRecencyScore({ lastUsedAt: usage?.lastUsedAt, now, halfLifeDays })
    const frequency = calculateFrequencyScore(usage?.count)

    const maxCount = toNonNegativeNumberOrZero(maxCountForNormalization)
    const denom = maxCount ? Math.log2(maxCount + 1) : 1
    const normalizedFrequency = denom ? Math.min(frequency / denom, 1) : 0

    const rw = toNonNegativeNumberOrZero(recencyWeight)
    const fw = toNonNegativeNumberOrZero(frequencyWeight)
    const totalWeight = rw + fw
    if (!totalWeight) {
        return recency
    }

    return (rw / totalWeight) * recency + (fw / totalWeight) * normalizedFrequency
}

/**
 * Coerces any input into the expected recentArtists shape:
 * { [artistId: string]: { count: number, lastUsedAt: number } }
 */
export const coerceRecentArtistsMap = (input) => {
    if (!isPlainObject(input)) {
        return {}
    }

    const out = {}
    for (const [rawArtistId, rawUsage] of Object.entries(input)) {
        const artistId = toPositiveIntOrNull(rawArtistId)
        if (!artistId) continue

        const usage = isPlainObject(rawUsage) ? rawUsage : {}
        const count = Math.floor(toNonNegativeNumberOrZero(usage.count))
        const lastUsedAt = toNonNegativeNumberOrZero(usage.lastUsedAt)

        out[String(artistId)] = {
            count,
            lastUsedAt,
        }
    }
    return out
}

export const upsertRecentArtistsUsage = ({ recentArtists, usedArtistId, now }) => {
    const usedId = toPositiveIntOrNull(usedArtistId)
    if (!usedId) return coerceRecentArtistsMap(recentArtists)

    const current = coerceRecentArtistsMap(recentArtists)
    const key = String(usedId)
    const existing = current[key] || { count: 0, lastUsedAt: 0 }

    return {
        ...current,
        [key]: {
            count: Math.floor(toNonNegativeNumberOrZero(existing.count)) + 1,
            lastUsedAt: toNonNegativeNumberOrZero(now) || Date.now(),
        },
    }
}

export const selectRecentArtistIds = ({
    recentArtists,
    max = 8,
    artistMap,
    now = Date.now(),
    scoring = DEFAULT_RECENT_USAGE_SCORING,
} = {}) => {
    const coerced = coerceRecentArtistsMap(recentArtists)
    const maxCount = Math.max(0, Math.floor(toNonNegativeNumberOrZero(max)))

    return Object.entries(coerced)
        .map(([artistIdStr, usage]) => {
            const artistId = toPositiveIntOrNull(artistIdStr)
            return {
                artistId,
                usage,
                score: calculateTotalScore(usage, { ...scoring, now }),
            }
        })
        .filter(({ artistId }) => {
            if (!artistId) return false
            if (!artistMap) return true
            const artist = artistMap[artistId]
            return Boolean(artist) && !artist.Ausgeschieden
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, maxCount)
        .map(({ artistId }) => artistId)
}

export const capRecentArtistsMap = ({
    recentArtists,
    maxEntries = 50,
    now = Date.now(),
    scoring = DEFAULT_RECENT_USAGE_SCORING,
} = {}) => {
    const coerced = coerceRecentArtistsMap(recentArtists)
    const limit = Math.max(0, Math.floor(toNonNegativeNumberOrZero(maxEntries)))
    if (!limit) return {}

    const keepIds = selectRecentArtistIds({
        recentArtists: coerced,
        max: limit,
        now,
        scoring,
    }).map(String)

    const out = {}
    for (const id of keepIds) {
        out[id] = coerced[id]
    }
    return out
}


